import { supabase } from '../client';

export type ScheduledJobRow = {
  id: string;
  org_id: string;
  job_type: string;
  payload: Record<string, unknown>;
  run_at: string;
  status: string;
  attempts: number;
  last_error: string | null;
  created_at: string;
  updated_at?: string;
};

/**
 * JOB-011: enqueue / cancel `job_publish` rows in ggj_b2b_scheduled_jobs for worker processing.
 * §9.14: Edge **`ggj_b2b_run-schedulers`** drains due `job_publish` rows (service role).
 */
export const scheduledJobService = {
  async listForOrg(orgId: string, opts?: { limit?: number; jobType?: string }): Promise<ScheduledJobRow[]> {
    const limit = Math.min(100, Math.max(1, opts?.limit ?? 25));
    let q = supabase
      .from('ggj_b2b_scheduled_jobs')
      .select('*')
      .eq('org_id', orgId)
      .order('run_at', { ascending: false })
      .limit(limit);
    if (opts?.jobType) q = q.eq('job_type', opts.jobType);
    const { data, error } = await q;
    if (error) throw error;
    return (data ?? []) as ScheduledJobRow[];
  },

  async cancelPendingPublishJobsForB2BJob(orgId: string, b2bJobId: string): Promise<void> {
    const { error } = await supabase
      .from('ggj_b2b_scheduled_jobs')
      .update({ status: 'cancelled' })
      .eq('org_id', orgId)
      .eq('job_type', 'job_publish')
      .eq('status', 'pending')
      .contains('payload', { b2b_job_id: b2bJobId });
    if (error) throw error;
  },

  async scheduleJobPublish(orgId: string, b2bJobId: string, runAtIso: string): Promise<void> {
    await this.cancelPendingPublishJobsForB2BJob(orgId, b2bJobId);
    const { data: sj, error: insErr } = await supabase
      .from('ggj_b2b_scheduled_jobs')
      .insert({
        org_id: orgId,
        job_type: 'job_publish',
        payload: { b2b_job_id: b2bJobId },
        run_at: runAtIso,
      })
      .select('id')
      .single();
    if (insErr) throw insErr;
    const { error: upErr } = await supabase
      .from('ggj_b2b_jobs')
      .update({
        publish_status: 'scheduled',
        scheduled_publish_at: runAtIso,
        scheduled_publish_job_id: sj.id,
      })
      .eq('id', b2bJobId)
      .eq('org_id', orgId);
    if (upErr) throw upErr;
  },

  async cancelPendingUnpublishJobsForB2BJob(orgId: string, b2bJobId: string): Promise<void> {
    const { error } = await supabase
      .from('ggj_b2b_scheduled_jobs')
      .update({ status: 'cancelled' })
      .eq('org_id', orgId)
      .eq('job_type', 'unpublish_job')
      .eq('status', 'pending')
      .contains('payload', { b2b_job_id: b2bJobId });
    if (error) throw error;
  },

  /**
   * §9.14: auto-close listing at run_at (publish_status → closed; optional job.status → closed).
   * Edge `ggj_b2b_run-schedulers` drains rows when job is published/paused and scheduled_unpublish_job_id matches.
   */
  async scheduleJobUnpublish(
    orgId: string,
    b2bJobId: string,
    runAtIso: string,
    opts?: { closePosition?: boolean }
  ): Promise<void> {
    await this.cancelPendingUnpublishJobsForB2BJob(orgId, b2bJobId);
    const closePosition = opts?.closePosition !== false;
    const { data: sj, error: insErr } = await supabase
      .from('ggj_b2b_scheduled_jobs')
      .insert({
        org_id: orgId,
        job_type: 'unpublish_job',
        payload: { b2b_job_id: b2bJobId, close_position: closePosition },
        run_at: runAtIso,
      })
      .select('id')
      .single();
    if (insErr) throw insErr;
    const { error: upErr } = await supabase
      .from('ggj_b2b_jobs')
      .update({
        scheduled_unpublish_at: runAtIso,
        scheduled_unpublish_job_id: sj.id,
      })
      .eq('id', b2bJobId)
      .eq('org_id', orgId);
    if (upErr) throw upErr;
  },

  /** §9.14: nudge approver when requisition stays in submitted / awaiting_client. */
  async scheduleReqReminder(orgId: string, requisitionId: string, runAtIso: string): Promise<void> {
    const { error } = await supabase.from('ggj_b2b_scheduled_jobs').insert({
      org_id: orgId,
      job_type: 'req_reminder',
      payload: { requisition_id: requisitionId },
      run_at: runAtIso,
    });
    if (error) throw error;
  },
};
